import { useState } from "react"
import { ErrorMessage } from "formik"
import { PencilSimple, X } from "@phosphor-icons/react"

const TagInput = ({
  name,
  label = "",
  placeholder = "",
  value = "",
  setFieldValue,
}) => {
  const [inputValue, setInputValue] = useState("")
  const [editingIndex, setEditingIndex] = useState(-1)
  const [editValue, setEditValue] = useState("")

  // Convert comma-separated string to array
  const tags = value
    ? value
        .split(",")
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0)
    : []

  const addTag = (tagValue) => {
    if (!tagValue.trim()) return

    const newTags = [...tags, tagValue.trim()]
    const newValue = newTags.join(",")
    setFieldValue(name, newValue)
    setInputValue("")
  }

  const removeTag = (indexToRemove) => {
    const newTags = tags.filter((_, index) => index !== indexToRemove)
    const newValue = newTags.join(",")
    setFieldValue(name, newValue)
  }

  const startEdit = (index) => {
    setEditingIndex(index)
    setEditValue(tags[index])
  }

  const saveEdit = () => {
    if (!editValue.trim()) {
      cancelEdit()
      return
    }

    const newTags = [...tags]
    newTags[editingIndex] = editValue.trim()
    const newValue = newTags.join(",")
    setFieldValue(name, newValue)
    setEditingIndex(-1)
    setEditValue("")
  }

  const cancelEdit = () => {
    setEditingIndex(-1)
    setEditValue("")
  }

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault()
      addTag(inputValue)
    }
  }

  const handleInputBlur = () => {
    if (inputValue.trim()) {
      addTag(inputValue)
    }
  }

  const handleEditKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault()
      saveEdit()
    } else if (e.key === "Escape") {
      e.preventDefault()
      cancelEdit()
    }
  }

  return (
    <div className="w-full">
      {label && (
        <label className="ml-1 mb-1 text-sm block" htmlFor={name}>
          {label}
        </label>
      )}

      <div className="w-full border border-slate-300 rounded-lg p-2 min-h-[44px] focus-within:border-secondary">
        {/* Tags display */}
        <div className="flex flex-wrap gap-2 mb-2">
          {tags.map((tag, index) => (
            <div
              key={index}
              className="group relative inline-flex items-center bg-blue-100 hover:bg-blue-200 text-blue-800 px-3 py-1 rounded-full text-sm transition-colors min-w-[60px]"
            >
              {editingIndex === index ? (
                <input
                  autoFocus
                  className="bg-transparent outline-none border-none p-0 m-0 text-sm min-w-[60px] max-w-[200px]"
                  type="text"
                  value={editValue}
                  onBlur={saveEdit}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={handleEditKeyDown}
                />
              ) : (
                <>
                  <span className="pr-1 text-center w-full">{tag}</span>
                  <div className="hidden group-hover:flex items-center gap-1 ml-1 absolute right-2">
                    <button
                      className="text-blue-600 hover:text-blue-800 p-1 bg-blue-100 hover:bg-blue-200 rounded"
                      type="button"
                      onClick={() => startEdit(index)}
                    >
                      <PencilSimple size={12} />
                    </button>
                    <button
                      className="text-red-500 hover:text-red-700 p-1 bg-blue-100 hover:bg-blue-200 rounded"
                      type="button"
                      onClick={() => removeTag(index)}
                    >
                      <X size={12} />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Input for new tags */}
        <input
          className="outline-none border-none text-gray-900 placeholder:text-gray-400 text-sm flex-1 min-w-[120px]"
          id={name}
          placeholder={
            tags.length === 0 || placeholder
              ? placeholder
              : "Add another keyword..."
          }
          type="text"
          value={inputValue}
          onBlur={handleInputBlur}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
        />
      </div>

      <ErrorMessage name={name}>
        {(msg) => <p className="text-rose-500 text-xs ml-2 mt-1">{msg}</p>}
      </ErrorMessage>

      <p className="text-gray-500 text-xs ml-2 mt-1">
        Press Enter or comma to add keywords. Hover over tags to edit or delete.
      </p>
    </div>
  )
}

export default TagInput
